import { IncidentWorkItemStatusSchema } from "@thalamos/common";

import type { ActionInput } from "../ActionInput.js";
import { calculateIncidentWorkItemStatus } from "../calculateIncidentWorkItemStatus.js";
import {
  incidentHasTransferRequest,
  incidentIsActive,
  isCurrentUserSupervisor,
  isPendingOwner,
  userIsIncidentOwner,
  userIsThalamosAdmin,
} from "../helpers.js";

type InitiateTransferAction = {
  canInitiate: boolean;
};

type RevokeTransferAction = {
  canRevoke: boolean;
};

type AcceptRejectTransferAction = {
  canAcceptReject: boolean;
};

type TransferActions = InitiateTransferAction &
  RevokeTransferAction &
  AcceptRejectTransferAction;

function incidentStatusAllowsTransfer(actionInput: ActionInput): boolean {
  const { incidentWorkItem } = actionInput;
  return (
    incidentIsActive(actionInput) ||
    calculateIncidentWorkItemStatus(incidentWorkItem).unwrapOr(
      IncidentWorkItemStatusSchema.enum.unknown,
    ) === IncidentWorkItemStatusSchema.enum.unknown
  );
}

function canInitiateTransfer(actionInput: ActionInput): boolean {
  return (
    incidentStatusAllowsTransfer(actionInput) &&
    (userIsIncidentOwner(actionInput) ||
      userIsThalamosAdmin(actionInput) ||
      isCurrentUserSupervisor(actionInput.user)) &&
    // can't initiate if there's already a transfer request
    !incidentHasTransferRequest(actionInput)
  );
}

function canRevokeTransfer(actionInput: ActionInput): boolean {
  return (
    incidentStatusAllowsTransfer(actionInput) &&
    (userIsIncidentOwner(actionInput) ||
      userIsThalamosAdmin(actionInput) ||
      isCurrentUserSupervisor(actionInput.user)) &&
    incidentHasTransferRequest(actionInput)
  );
}

function canAcceptRejectTransfer(actionInput: ActionInput): boolean {
  return (
    incidentStatusAllowsTransfer(actionInput) &&
    isPendingOwner(actionInput.incidentWorkItem, actionInput.user.id)
  );
}

export function calculateTransferActions(
  actionInput: ActionInput,
): TransferActions {
  return {
    canInitiate: canInitiateTransfer(actionInput),
    canRevoke: canRevokeTransfer(actionInput),
    canAcceptReject: canAcceptRejectTransfer(actionInput),
  };
}
